import { combineReducers } from 'redux';
import uuidV4 from 'uuid/v4';

const sortFolders = folderParam => {
  return folderParam.sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
};

export const untitledFolder = {
  id: '1',
  name: 'Untitled Folder',
  buildings: [],
  sites: [],
  counties: [],
  states: [],
  communities: [],
  regions: [],
};

export const initialFoldersState = {
  active: '1',
  savedFolders: [untitledFolder],
};

export function folders(state = initialFoldersState, action) {
  switch (action.type) {
    case 'ADD_FOLDER': {
      const newFolderId = uuidV4();
      const nextState = { ...state };
      if (action.updateActiveFolder) {
        nextState.active = newFolderId;
      }
      nextState.savedFolders = [...nextState.savedFolders, {
        id: newFolderId,
        name: action.name,
        buildings: [],
        sites: [],
        counties: [],
        states: [],
        communities: [],
        regions: [],
      }];
      nextState.savedFolders = sortFolders(nextState.savedFolders);
      return nextState;
    }
    case 'REMOVE_FOLDER': {
      const folderIndex = state.savedFolders.findIndex(f => f.id === action.id);
      const nextSavedFolders = [
        ...state.savedFolders.slice(0, folderIndex),
        ...state.savedFolders.slice(folderIndex + 1),
      ];
      if (nextSavedFolders.length === 0) {
        return { ...initialFoldersState };
      }
      const [firstFolder] = nextSavedFolders;
      const nextActive = firstFolder ? firstFolder.id : '';
      return {
        active: nextActive,
        savedFolders: sortFolders(nextSavedFolders),
      };
    }
    case 'SELECT_FOLDER': {
      return {
        ...state,
        active: action.id,
      };
    }
    case 'UPDATE_FOLDER': {
      const folderIndex = state.savedFolders.findIndex(f => f.id === action.id);
      const folder = state.savedFolders[folderIndex];
      const nextSavedFolders = [
        ...state.savedFolders.slice(0, folderIndex),
        { ...folder, name: action.name },
        ...state.savedFolders.slice(folderIndex + 1),
      ];
      return {
        active: action.id,
        savedFolders: sortFolders(nextSavedFolders),
      };
    }
    case 'SAVE_ITEMS': {
      const itemIds = action.items.map(p => p._id || p.id);
      if (action.folderId === '--all--') {
        return {
          ...state,
          active: state.savedFolders[0].id,
          savedFolders: state.savedFolders.map(folder => ({
            ...folder,
            [action.itemType]: []
              .concat(itemIds)
              .concat(folder[action.itemType])
              .reduce((ids, id) => {
                if (!id || ids.indexOf(id) > -1) {
                  return ids;
                }
                return [...ids, id];
              }, []),
          })),
        };
      }

      const [folder = {}] = state.savedFolders.filter(f => f.id === action.folderId);
      const otherFolders = state.savedFolders.filter(f => f.id !== action.folderId);
      if (!folder.id) {
        return state;
      }
      const nextState = { ...state };
      const nextFolder = {
        ...folder,
        [action.itemType]: []
          .concat(itemIds)
          .concat(folder[action.itemType])
          .reduce((ids, id) => {
            if (!id || ids.indexOf(id) > -1) {
              return ids;
            }
            return [...ids, id];
          }, []),
      };
      nextState.savedFolders = sortFolders(otherFolders.concat(nextFolder));
      return nextState;
    }
    case 'REMOVE_ITEMS': {
      const itemIds = action.items.map(p => p._id);
      if (action.folderId === '--all--') {
        return {
          ...state,
          active: state.savedFolders[0].id,
          savedFolders: state.savedFolders.map(folder => ({
            ...folder,
            [action.itemType]: []
              .concat(folder[action.itemType])
              .filter(id => id && itemIds.indexOf(id) === -1),
          })),
        };
      }

      const [folder = {}] = state.savedFolders.filter(f => f.id === action.folderId);
      const otherFolders = state.savedFolders.filter(f => f.id !== action.folderId);
      if (!folder.id) {
        return state;
      }
      const nextState = { ...state };
      const nextFolder = {
        ...folder,
        [action.itemType]: []
          .concat(folder[action.itemType])
          .filter(id => id && itemIds.indexOf(id) === -1),
      };
      nextState.savedFolders = sortFolders(otherFolders.concat(nextFolder));
      return nextState;
    }
    default:
      return state;
  }
}

const initialItemsState = {
  buildings: [],
  sites: [],
  counties: [],
  states: [],
  communities: [],
  regions: [],
};

export function items(state = initialItemsState, action) {
  if (state[action.itemType] === undefined) {
    return state;
  }

  switch (action.type) {
    case 'ADD_FAVORITE_ITEMS': {
      return { ...state, [action.itemType]: action.items };
    }
    case 'SAVE_ITEMS': {
      // Add the folder ids to the items so that when we remove an item duplicated in multiple
      // favorite folders
      let modifiedItems = [];
      if (action.folderId === '--all--') {
        const savedFolderIds = action.savedFolders.map(sf => sf.id);
        modifiedItems = action.items.map(ai => ({ ...ai, foldersSavedIn: savedFolderIds }));
      } else {
        modifiedItems = action.items.map(ai => ({ ...ai, foldersSavedIn: [action.folderId] }));
      }

      // If we haven't lazy loaded other items yet, don't add it.
      // This fixes bug where -
      //   1) Have some favorites
      //   2) Refresh page while on buildings or sites route
      //   3) Add a favorite
      //   4) Navigate to favorites
      //   5) Observe newly added item exists, but not prior ones until refresh
      // This fix works because length === 0 will trigger fetch of all saved items

      // This was commented this out 19 feb 2019, this bug could not be reproduced in staging
      // This code is being left commented in case this bug resurfaces.

      // if (state[action.itemType].length === 0) {
      //   return state;
      // }

      return { ...state,
        [action.itemType]: []
        .concat(modifiedItems)
        .concat(state[action.itemType])
        .reduce((acc, item) => {
          if (!item || !item._id || acc.find(i => i._id === item._id)) {
            return acc;
          }
          return [...acc, item];
        }, []),
      };
    }
    case 'REMOVE_ITEMS': {
      // If there are multiple folders dont worry about removing it from items since
      // it could be in another folder
      if (action.savedFolders && action.savedFolders.length > 1) {
        return state;
      }

      const itemIds = action.items.map(p => p._id || p.id);

      if (itemIds.length === 1) {
        if (action.folderId === '--all--') {
          return { ...state,
            [action.itemType]: []
            .concat(state[action.itemType])
            .filter(item => item && item._id && itemIds.indexOf(item._id) === -1),
          };
        }
        return { ...state,
          [action.itemType]: []
          .concat(state[action.itemType])
          .filter(item => item && item._id && itemIds.indexOf(item._id) === -1),
        };
      }

      // this section is if they are removing all items from one folder
      return { ...state,
        [action.itemType]: []
        .concat(state[action.itemType])
        .filter(item => item && item._id && itemIds.indexOf(item._id) === -1),
      };
    }
    default:
      return state;
  }
}

export function dontShow(state = false, action) {
  switch (action.type) {
    case 'DONT_SHOW': {
      return action.value;
    }
    default:
      return state;
  }
}

export default combineReducers({
  folders,
  items,
  dontShow,
});



// WEBPACK FOOTER //
// ./src/store/reducers/favorites.js